import { useRoute } from '#imports'
import { type Ref, computed, ref } from 'vue'

import type { FileUploadValue } from '@backmarket/http-api/src/api-specs-reviews/types/review-form'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { objectKeys } from '@backmarket/utils/object/objectKeys'

import type {
  GetReviewCollectionResponse,
  StepId,
} from '~/api/reviews/types/GetReviewCollectionResponse'

import { useFormStep } from '../../review-collection/composables/useFormStep'
import type {
  FileValue,
  ReviewForm,
  ReviewFormQuestion,
} from '../models/review-form'

import { useBuildForm } from './useBuildForm'
import { usePostOrderReviewAttachments } from './usePostOrderReviewAttachments'
import { useReviewCollectionTracking } from './useReviewCollectionTracking'

function shouldSkipSendingRequest({
  newValues,
  savedValues,
  formStep,
  formStepId,
  lastSubmittedStep,
  attachments,
}: {
  newValues: ReviewForm
  savedValues: ReviewForm | undefined
  formStep: number
  formStepId: StepId | null
  lastSubmittedStep: number
  attachments: File[]
}) {
  if (!savedValues) return false

  const hasIdenticalValues = objectKeys(newValues).every((key) => {
    const newValue = newValues[key]
    const savedValue = savedValues[key]

    if (formStepId === 'attachments') {
      return isEqual(attachments, savedValue)
    }

    return isEqual(newValue, savedValue)
  })

  const hasAlreadySubmittedThisStep = formStep <= lastSubmittedStep

  return hasIdenticalValues || hasAlreadySubmittedThisStep
}

function useLiveAverageRate({
  reviewCollectionData,
}: {
  reviewCollectionData: Ref<GetReviewCollectionResponse | undefined>
}) {
  const initialAverageRate = computed(
    () => reviewCollectionData.value?.averageRate,
  )

  const updatedAverageRate = ref<number>()

  const averageRate = computed(
    () => updatedAverageRate.value ?? initialAverageRate.value,
  )

  return { averageRate, updatedAverageRate }
}

export function useReviewCollectionForm(
  data: Ref<GetReviewCollectionResponse | undefined>,
  submitFormToApi: ({
    stepValues,
    questions,
    orderlineId,
    authToken,
  }: {
    stepValues: ReviewForm
    questions: ReviewFormQuestion[]
    orderlineId: string | string[]
    authToken?: string
  }) => Promise<[number, undefined] | [undefined, Error]>,
) {
  const i18n = useI18n()
  const { params, query } = useRoute()
  const { trackStepSubmission } = useReviewCollectionTracking()
  const uploadAttachments = usePostOrderReviewAttachments()

  const {
    increment: incrementStep,
    formStep,
    decrement: decrementStep,
    isFirstStep,
    goToFirstStep,
  } = useFormStep()

  const maxStepSubmitted = ref(-1)
  const submitPending = ref(false)

  const { averageRate, updatedAverageRate } = useLiveAverageRate({
    reviewCollectionData: data,
  })

  const {
    form,
    layout,
    questions,
    validators,
    isSummaryStep,
    saveValuesInForm,
    buildFormForCurrentStep,
  } = useBuildForm(data, formStep, i18n)

  const goToPreviousStep = (values: ReviewForm) => {
    saveValuesInForm(values)
    decrementStep()
  }

  const goToNextStep = () => {
    incrementStep()
  }

  async function submitStep({
    formStepId,
    values,
    attachments,
  }: {
    formStepId: StepId | null
    values: ReviewForm
    attachments: File[]
  }) {
    if (
      shouldSkipSendingRequest({
        savedValues: form.value,
        newValues: values,
        formStep: formStep.value,
        formStepId,
        attachments,
        lastSubmittedStep: maxStepSubmitted.value,
      })
    ) {
      incrementStep()

      return
    }

    submitPending.value = true

    const stepValues = { ...values }
    let attachmentsHandles: FileUploadValue[] | FileValue[] = []

    if (formStepId === 'attachments') {
      attachmentsHandles = await uploadAttachments({
        attachmentFiles: attachments,
        orderlineId: params.orderlineId,
        authToken: query.authToken as string,
      })

      stepValues.review_attachments = attachmentsHandles
    }

    const [newAverageRate, submitFormError] = await submitFormToApi({
      stepValues,
      questions: questions.value,
      orderlineId: params.orderlineId,
      authToken: query.authToken as string,
    })

    saveValuesInForm(values, attachmentsHandles)

    submitPending.value = false

    if (!submitFormError) {
      maxStepSubmitted.value = Math.max(formStep.value, maxStepSubmitted.value)

      trackStepSubmission({
        values: stepValues,
        step: formStep.value,
        stepId: formStepId,
        questions: questions.value,
        product: data.value?.product,
        averageRate: newAverageRate || 0,
        fileCount: attachments.length || 0,
      })
      incrementStep()
    }

    // eslint-disable-next-line consistent-return
    return newAverageRate
  }

  return {
    formStep,
    isSummaryStep,
    averageRate,
    updatedAverageRate,
    questions,
    validators,
    form,
    layout,
    goToPreviousStep,
    goToFirstStep,
    goToNextStep,
    isFirstStep,
    submitStep,
    submitPending,
    buildFormForCurrentStep,
  }
}
